import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Star } from "@phosphor-icons/react";

const BLANK_PLAN = {
  name: "", price: 29, currency: "USD", period: "mensual",
  description: "", features: [""], cta_label: "Empezar",
  highlight: false, sort_order: 1, active: true,
};

export default function AdminPlans({ headers }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans", { headers });
      setPlans(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando planes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => setEditing({ ...BLANK_PLAN });
  const openEdit = (p) => setEditing({ ...p, features: p.features?.length ? [...p.features] : [""] });
  const close = () => setEditing(null);

  const save = async () => {
    const body = { ...editing, features: (editing.features || []).map(f => f.trim()).filter(Boolean), price: Number(editing.price), sort_order: Number(editing.sort_order) };
    try {
      if (editing.id) {
        await api.patch(`/admin/plans/${editing.id}`, body, { headers });
        toast.success("Plan actualizado");
      } else {
        await api.post("/admin/plans", body, { headers });
        toast.success("Plan creado");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el plan "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/plans/${p.id}`, { headers });
      toast.success("Plan eliminado");
      load();
    } catch (err) {
      toast.error("Error");
    }
  };

  return (
    <section data-testid="admin-plans">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Planes y precios</div>
          <h2 className="font-display text-xl font-medium tracking-tight">
            Catálogo público ({plans.length})
          </h2>
        </div>
        <Button
          onClick={openCreate}
          data-testid="plan-new-button"
          className="bg-[#5E6AD2] hover:bg-[#4F58B8] text-white"
        >
          <Plus size={14} className="mr-1.5" /> Nuevo plan
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-[#71717A]">Cargando...</div>
      ) : plans.length === 0 ? (
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-10 text-center text-sm text-[#71717A]">
          Sin planes aún. Crea el primero.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {plans.map(p => (
            <div
              key={p.id}
              data-testid={`admin-plan-${p.name}`}
              className={`bg-[#121214] border rounded-lg p-5 ${
                p.highlight ? "border-[#5E6AD2]/50" : "border-[#27272A]"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display text-lg font-medium flex items-center gap-2">
                    {p.name}
                    {p.highlight && <Star size={12} weight="fill" className="text-[#5E6AD2]" />}
                  </div>
                  <div className="text-[11px] text-[#71717A]">orden {p.sort_order}</div>
                </div>
                {!p.active && (
                  <Badge className="bg-[#71717A]/15 text-[#71717A] border-[#71717A]/30 text-[10px]">
                    inactivo
                  </Badge>
                )}
              </div>
              <div className="mb-3">
                <span className="font-display text-3xl font-medium">${p.price}</span>
                <span className="text-xs text-[#71717A] ml-2">{p.currency}/{p.period}</span>
              </div>
              {p.description && <p className="text-xs text-[#A1A1AA] mb-3 line-clamp-2">{p.description}</p>}
              <div className="text-[10px] text-[#71717A] mb-3">{(p.features || []).length} características</div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => openEdit(p)}
                  data-testid={`plan-edit-${p.name}`}
                  className="flex-1 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-white"
                >
                  <PencilSimple size={12} className="mr-1.5" /> Editar
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => remove(p)}
                  data-testid={`plan-delete-${p.name}`}
                  className="border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#EF4444]"
                >
                  <Trash size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-[#FAFAFA] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing?.id ? "Editar plan" : "Nuevo plan"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Nombre</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="plan-name-input" className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Precio</Label>
                  <Input type="number" step="0.01" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} data-testid="plan-price-input" className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Moneda</Label>
                  <Input value={editing.currency} onChange={e => setEditing({ ...editing, currency: e.target.value })} className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Período</Label>
                  <Input value={editing.period} onChange={e => setEditing({ ...editing, period: e.target.value })} placeholder="mensual / anual" className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Descripción corta</Label>
                <Textarea rows={2} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className="mt-1.5 bg-[#09090B] border-[#27272A]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Características (una por línea)</Label>
                <Textarea
                  rows={6}
                  value={(editing.features || []).join("\n")}
                  onChange={e => setEditing({ ...editing, features: e.target.value.split("\n") })}
                  data-testid="plan-features-input"
                  className="mt-1.5 bg-[#09090B] border-[#27272A] font-mono text-xs"
                  placeholder={"1 número WhatsApp\n500 mensajes/mes\nSoporte email"}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Botón (CTA)</Label>
                  <Input value={editing.cta_label} onChange={e => setEditing({ ...editing, cta_label: e.target.value })} className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#71717A]">Orden</Label>
                  <Input type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: e.target.value })} className="mt-1.5 bg-[#09090B] border-[#27272A]" />
                </div>
                <div className="flex flex-col gap-2 justify-end">
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Destacado</span>
                    <Switch checked={!!editing.highlight} onCheckedChange={v => setEditing({ ...editing, highlight: v })} data-testid="plan-highlight-switch" />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Activo</span>
                    <Switch checked={editing.active !== false} onCheckedChange={v => setEditing({ ...editing, active: v })} data-testid="plan-active-switch" />
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} className="border-[#27272A] bg-transparent text-white">
              Cancelar
            </Button>
            <Button onClick={save} disabled={!editing?.name} data-testid="plan-save-button" className="bg-[#5E6AD2] hover:bg-[#4F58B8] text-white">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
